import { makeAutoObservable } from 'mobx'
import {
  getLevelFromXP,
  xpToReachLevel,
  xpForNextLevel,
  xpProgressInLevel,
  computeXPFromGame,
} from '../utils/xpLevels'

export type GameId =
  | 'memoji'
  | 'findmoji'
  | 'hitmoji'
  | 'chessmoji'
  | 'evoji'
  | 'quizmoji'
  | 'timeline'

export interface GameStats {
  bestScore: number
  maxLevel: number
  gamesPlayed: number
}

const STORAGE_KEY = 'planetmoji_gamification'

function loadStored(): { xp: number; gameStats: Record<string, GameStats> } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const data = JSON.parse(raw)
      return {
        xp: typeof data.xp === 'number' ? data.xp : 0,
        gameStats: data.gameStats && typeof data.gameStats === 'object' ? data.gameStats : {},
      }
    }
  } catch (e) {
    console.warn('Failed to load gamification from storage')
  }
  return { xp: 0, gameStats: {} }
}

function save(xp: number, gameStats: Record<string, GameStats>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ xp, gameStats }))
  } catch (e) {
    console.warn('Failed to save gamification')
  }
}

class GamificationStore {
  xp: number = 0
  gameStats: Record<string, GameStats> = {}
  /** Si > 0, se acaba de subir de nivel (timestamp para mostrar celebración). */
  lastLevelUp: number = 0

  constructor() {
    const stored = loadStored()
    this.xp = stored.xp
    this.gameStats = stored.gameStats
    makeAutoObservable(this)
  }

  get level(): number {
    return getLevelFromXP(this.xp)
  }

  /** Progreso hacia el siguiente nivel (0-1). */
  get xpProgress(): number {
    return xpProgressInLevel(this.xp).progress
  }

  get xpInCurrentLevel(): { current: number; needed: number } {
    const p = xpProgressInLevel(this.xp)
    return { current: p.current, needed: p.needed }
  }

  get xpToNextLevel(): number {
    return xpForNextLevel(this.level)
  }

  getStatsForGame(gameId: string): GameStats {
    return (
      this.gameStats[gameId] || {
        bestScore: 0,
        maxLevel: 0,
        gamesPlayed: 0,
      }
    )
  }

  /** Añade XP. Retorna el nuevo nivel si hubo subida de nivel, 0 si no. */
  addXP(amount: number): number {
    if (amount <= 0) return 0
    const prevLevel = this.level
    this.xp += amount
    const newLevel = this.level
    save(this.xp, this.gameStats)
    if (newLevel > prevLevel) {
      this.lastLevelUp = Date.now()
      return newLevel
    }
    return 0
  }

  /** Registra el resultado de una partida y otorga XP. Retorna XP otorgado y si subió de nivel. */
  recordGameResult(
    gameId: GameId,
    score: number,
    levelReached: number,
    won: boolean
  ): { xpEarned: number; leveledUp: number } {
    const stats = this.getStatsForGame(gameId)
    const newStats: GameStats = {
      bestScore: Math.max(stats.bestScore, score),
      maxLevel: Math.max(stats.maxLevel, levelReached),
      gamesPlayed: stats.gamesPlayed + 1,
    }
    this.gameStats[gameId] = newStats
    const xpEarned = computeXPFromGame(score, levelReached, won)
    const leveledUp = this.addXP(xpEarned)
    save(this.xp, this.gameStats)
    return { xpEarned, leveledUp }
  }

  clearLevelUpCelebration() {
    this.lastLevelUp = 0
  }
}

export const gamificationStore = new GamificationStore()
